// src/Layout/Layout.js

import React, { useContext, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import PublicRoutes from "./PublicRoutes";
import PrivateRoutes from "./PrivateRoutes";
import Alert from "../Components/Alert/Alert";
import { AuthContext } from "./AuthContext";
import { capturePendingDeepLink } from "../Utils/postAuthRedirect";
import { captureFocusRedirect } from "../Utils/focusRedirect";
import TrinityApp from "../Pages/Trinity/TrinityApp";
import { isTrinityPortal } from "../Utils/adminUserTypes";

// Paths that only exist behind auth but are reachable straight from an
// emailed "Complete Now" deep link (see pkg/scheduled_email_queue/adapters.go's
// LinkFmt). Captured here, before the isAuthenticated split below hands off
// to PublicRoutes, whose own catch-all otherwise drops the path+query and
// strands the click on a bare Login page.
const DEEP_LINK_PATHS = ["/adminlist"];

const Layout = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isTrinityPortal()) return;
    if (isAuthenticated) return;
    // A signed-out click on a Focus page or a chat session link (BUG-11): keep the path for
    // handleRedirection, then land on Login.
    if (captureFocusRedirect(location.pathname, location.search)) {
      navigate('/', { replace: true });
      return;
    }
    if (!DEEP_LINK_PATHS.includes(location.pathname)) return;

    const params = new URLSearchParams(location.search);
    if (!params.has("assignment")) return;

    // su=<signup_code> means the recipient hasn't finished signup yet -
    // route them through the invitation flow instead of Login. Either way,
    // stash the plain deep link (without su) for handleRedirection() to
    // restore once the user is authenticated.
    const signupCode = params.get("su");
    params.delete("su");
    const target = `${location.pathname}?${params.toString()}`;
    capturePendingDeepLink(target);

    if (signupCode) {
      navigate(`/register/invitation/${signupCode}`, { replace: true });
    } else {
      // Explicitly land on Login rather than relying on PublicRoutes' "*"
      // catch-all (some email clients open the deep link in a way that
      // leaves the path sticky until we navigate).
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, location.pathname, location.search, navigate]);

  if (isTrinityPortal()) {
    return <TrinityApp />;
  }

  return (
    <>
      <Routes>
        {/* Conditionally render routes based on authentication */}
        {isAuthenticated ? (
          <Route path="/*" element={<PrivateRoutes />} />
        ) : (
          <Route path="/*" element={<PublicRoutes />} />
        )}

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      <Alert />
    </>
  );
};

export default Layout;
